/**
 * Менеджер State Machine для CSElement
 */

import { StateMachineManager, IStateMachine, StateMachineConfig, TransitionResult } from '../types/state-machine-interfaces';
import { ICSElement } from '../types/interfaces';
import { StateMachine } from './StateMachine';

/**
 * Реализация менеджера State Machine
 */
export class StateMachineManagerImpl implements StateMachineManager {
  private _stateMachines: Map<string, IStateMachine>;

  constructor() {
    this._stateMachines = new Map();
  }

  /**
   * Создать state machine для элемента
   */
  create(element: ICSElement, config: StateMachineConfig): IStateMachine {
    // Проверяем, что у элемента еще нет state machine
    if (this._stateMachines.has(element.id)) {
      throw new Error(`State Machine для элемента "${element.id}" уже существует`);
    }

    // Создаем новый state machine
    const stateMachine = new StateMachine(element, config);
    
    // Сохраняем в реестре
    this._stateMachines.set(element.id, stateMachine);

    console.log(`🔄 State Machine "${config.name}" создан для элемента "${element.id}"`);
    
    return stateMachine;
  }

  /**
   * Получить state machine элемента
   */
  get(elementId: string): IStateMachine | undefined {
    return this._stateMachines.get(elementId);
  }

  /**
   * Проверить, есть ли state machine для элемента
   */
  has(elementId: string): boolean {
    return this._stateMachines.has(elementId);
  }

  /**
   * Удалить state machine элемента
   */
  remove(elementId: string): boolean {
    const stateMachine = this._stateMachines.get(elementId);
    if (stateMachine) {
      // Уничтожаем state machine
      stateMachine.destroy();
      
      // Удаляем из реестра
      this._stateMachines.delete(elementId);
      
      console.log(`🔄 State Machine для элемента "${elementId}" удален`);
      return true;
    }
    
    return false;
  }

  /**
   * Получить все state machines
   */
  getAll(): Map<string, IStateMachine> {
    return new Map(this._stateMachines);
  }

  /**
   * Очистить все state machines
   */
  clear(): void {
    // Уничтожаем все state machines
    for (const stateMachine of this._stateMachines.values()) {
      stateMachine.destroy();
    }
    
    // Очищаем реестр
    this._stateMachines.clear();
    
    console.log('🔄 Все State Machines очищены');
  }

  /**
   * Получить статистику
   */
  getStats(): {
    total: number;
    byState: Map<string, number>;
    byName: Map<string, number>;
  } {
    const stats = {
      total: this._stateMachines.size,
      byState: new Map<string, number>(),
      byName: new Map<string, number>()
    };

    for (const sm of this._stateMachines.values()) {
      // Статистика по состояниям
      const currentState = sm.currentState;
      stats.byState.set(currentState, (stats.byState.get(currentState) || 0) + 1);
      
      // Статистика по именам
      const name = sm.name;
      stats.byName.set(name, (stats.byName.get(name) || 0) + 1);
    }

    return stats;
  }

  /**
   * Найти state machines по критериям
   */
  findByState(stateName: string): IStateMachine[] {
    return Array.from(this._stateMachines.values())
      .filter(sm => sm.currentState === stateName);
  }

  /**
   * Найти state machines по имени
   */
  findByName(name: string): IStateMachine[] {
    return Array.from(this._stateMachines.values())
      .filter(sm => sm.name === name);
  }

  /**
   * Отправить событие всем state machines
   */
  async broadcastEvent(event: string, payload?: any): Promise<Map<string, any>> {
    const results = new Map<string, any>();
    
    for (const [elementId, sm] of this._stateMachines) {
      try {
        const result = await sm.send(event, payload);
        results.set(elementId, result);
      } catch (error) {
        results.set(elementId, { error: error instanceof Error ? error.message : String(error) });
      }
    }
    
    return results;
  }

  /**
   * Отправить событие state machines в определенном состоянии
   */
  async sendToState(stateName: string, event: string, payload?: any): Promise<Map<string, TransitionResult>> {
    const results = new Map<string, TransitionResult>();
    const targetMachines = this.findByState(stateName);
    
    for (const sm of targetMachines) {
      try {
        const result = await sm.send(event, payload);
        results.set(sm.context.element.id, result);
      } catch (error) {
        results.set(sm.context.element.id, {
          success: false,
          fromState: sm.currentState,
          toState: sm.currentState,
          event: { type: event, payload, timestamp: Date.now() },
          executedActions: [],
          error: error instanceof Error ? error.message : String(error),
          duration: 0
        });
      }
    }
    
    return results;
  }
} 